/**
 * Lightweight analytics tracker for YUCCA Flowers.
 * Sends events to /api/analytics/event (same domain, no external scripts).
 */

const API = "/api";
import { CITIES } from "@/cityConfig";

// Generate a session ID (persists per browser tab session)
function getSessionId() {
  let sid = sessionStorage.getItem("yucca_sid");
  if (!sid) {
    sid = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem("yucca_sid", sid);
  }
  return sid;
}

// Detect device type
function getDevice() {
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPad/i.test(ua)) return "mobile";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  return "desktop";
}

// Detect traffic source from referrer (falls back to utm_source when referrer is stripped)
function getSource() {
  const ref = document.referrer;
  let utm = "";
  try {
    utm = (new URLSearchParams(window.location.search).get("utm_source") || "").toLowerCase();
  } catch {
    utm = "";
  }
  const classify = (host) => {
    if (host.includes("chatgpt") || host.includes("openai")) return "chatgpt";
    if (host.includes("perplexity")) return "perplexity";
    if (host.includes("claude")) return "claude";
    if (host.includes("gemini.google") || host.includes("bard.google")) return "gemini";
    if (host.includes("copilot")) return "copilot";
    if (host.includes("bing")) return "bing";
    if (host.includes("duckduckgo")) return "duckduckgo";
    if (host.includes("google")) return "google";
    if (host.includes("yandex") || host === "ya.ru" || host.includes("dzen.ru")) return "yandex";
    if (host.includes("t.me") || host.includes("telegram")) return "telegram";
    if (host.includes("wa.me") || host.includes("whatsapp")) return "whatsapp";
    if (host.includes("facebook") || host.includes("fb.")) return "facebook";
    if (host.includes("instagram")) return "instagram";
    if (host.includes("vk.com")) return "vk";
    if (host.includes("yuccaflower.com")) return "internal";
    return host;
  };
  if (!ref) return utm ? classify(utm) : "direct";
  try {
    return classify(new URL(ref).hostname.toLowerCase());
  } catch {
    return "other";
  }
}

// Extract city from URL path
function getCityFromPath(path) {
  const match = path.match(/^\/([a-z0-9-]+)/);
  if (match && CITIES[match[1]]) return match[1];
  return "";
}

// Send event (fire-and-forget, never blocks UI)
function sendEvent(data) {
  try {
    const payload = {
      ...data,
      session: getSessionId(),
      device: getDevice(),
      source: getSource(),
      referrer: document.referrer,
      lang: localStorage.getItem("yucca_lang") || "ru",
    };

    // Use sendBeacon for reliability (works even on page unload)
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API}/analytics/event`,
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
    } else {
      fetch(`${API}/analytics/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never throw — analytics should never break the site
  }
}

// Track page view
export function trackPageView(page, city) {
  sendEvent({
    type: "pageview",
    page: page || window.location.pathname,
    city: city || getCityFromPath(window.location.pathname),
  });
}

// Track button click
export function trackClick(button, city) {
  sendEvent({
    type: "click",
    page: window.location.pathname,
    button,
    city: city || getCityFromPath(window.location.pathname),
  });
}
